// disegno.ts — disegna un fotogramma della clip: video (sopra e sotto) più le sovrimpressioni del marchio.
// Tutto vettoriale e originale: niente immagini esterne a parte il simbolo passato dall'app (comune/icona.svg).
import type { Montaggio, Segmento } from "./montaggio.ts";
import type { OpzioniClip } from "./tipi.ts";
import qrcode from "qrcode-generator";

export type Contesto2D = CanvasRenderingContext2D;

export interface Grafica {
  simbolo: CanvasImageSource | null;
  /** v037: il QR del link nel finale */
  qr?: CanvasImageSource | null;
}

/** Lato della tela su cui il simbolo si disegna una volta sola (v035). */
const LATO_SIMBOLO = 512;

/** Carica il simbolo SVG (testo) come immagine. Se non si carica, il marchio si disegna senza. */
export async function caricaSimbolo(svg: string): Promise<CanvasImageSource | null> {
  if (!svg.trim()) return null;
  const img = new Image();
  img.decoding = "async";
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    img.src = url;
    await img.decode();
    // v035: il simbolo ora è l'icona dello store (un'immagine dentro l'SVG): ridisegnare un SVG a ogni fotogramma
    // costa moltissimo, quindi si disegna una volta su una tela e la clip usa quella.
    if (typeof document === "undefined") return img;
    const tela = document.createElement("canvas");
    tela.width = LATO_SIMBOLO;
    tela.height = LATO_SIMBOLO;
    const c = tela.getContext("2d");
    if (!c) return img;
    c.imageSmoothingQuality = "high";
    c.drawImage(img, 0, 0, LATO_SIMBOLO, LATO_SIMBOLO);
    return tela;
  } catch {
    return null;
  } finally {
    // L'immagine decodificata resta utilizzabile anche dopo la revoca dell'URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Luminanza relativa (0 nero, 1 bianco) di un colore CSS #rgb/#rrggbb; per altri formati 0.5. */
export function luminanza(colore: string): number {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colore.trim());
  if (!m) return 0.5;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const canale = (i: number) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canale(0) + 0.7152 * canale(2) + 0.0722 * canale(4);
}

const SCURO = "#14101F";
const CHIARO = "#FFFFFF";
const contrasto = (fondo: string) => (luminanza(fondo) > 0.35 ? SCURO : CHIARO);

function rettangoloTondo(ctx: Contesto2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Imposta un font che fa stare `testo` in `larghezzaMassima`, partendo da `px` e scendendo. */
function adatta(ctx: Contesto2D, testo: string, famiglia: string, peso: number, px: number, larghezzaMassima: number): number {
  let dim = px;
  for (let i = 0; i < 30; i++) {
    ctx.font = `${peso} ${Math.round(dim)}px ${famiglia}`;
    if (ctx.measureText(testo).width <= larghezzaMassima) break;
    dim *= 0.92;
  }
  return dim;
}

const facile = (x: number) => 1 - (1 - x) ** 3;
const rimbalzo = (x: number) => {
  // sale oltre 1 e torna (effetto timbro)
  if (x >= 1) return 1;
  return 1 + 0.35 * Math.sin(x * Math.PI) * (1 - x) + (x < 0.4 ? -(1 - x / 0.4) * 0.6 : 0);
};

export interface StatoFotogramma {
  montaggio: Montaggio;
  segmento: Segmento;
  dentroMs: number; // tempo dall'inizio del segmento
  tClipMs: number;
  sorgenteMs: number;
  /** v042: partita a 4 — chi ha riso e chi ha vinto (indici dei riquadri 0…3) */
  celle?: { riso: number | null; vinto: number | null };
}

/** Disegna le sovrimpressioni sopra il video già disegnato. */
export function disegnaSovrimpressioni(ctx: Contesto2D, o: OpzioniClip, g: Grafica, s: StatoFotogramma): void {
  const W = o.larghezza;
  const H = o.altezza;
  const metà = H / 2;
  const u = W / 720; // unità: tutto è pensato per 720×1280 e scalato
  const seg = s.segmento;
  const sovr = seg.sovrimpressioni;
  ctx.save();
  ctx.direction = o.rtl ? "rtl" : "ltr";
  ctx.textBaseline = "middle";

  if (seg.tipo === "finale") {
    disegnaFinale(ctx, o, g, s.dentroMs, seg.durataMs);
    ctx.restore();
    return;
  }

  // Linea che separa le due metà (v038: nel video «solo mio» c'è una faccia sola, niente linea).
  if (o.griglia) {
    // v042: partita a 4: una croce tra i quattro riquadri
    ctx.fillStyle = o.colori.primario;
    ctx.fillRect(0, metà - 3 * u, W, 6 * u);
    ctx.fillRect(W / 2 - 3 * u, 0, 6 * u, H);
  } else if (!o.solo) {
    ctx.fillStyle = o.colori.primario;
    ctx.fillRect(0, metà - 3 * u, W, 6 * u);
  }

  if (sovr.includes("rallentatore")) {
    // Vignetta e bordi a «cinema»: il tempo si allunga.
    const k = Math.min(1, s.dentroMs / 300);
    const vign = ctx.createRadialGradient(W / 2, metà, W * 0.35, W / 2, metà, H * 0.75);
    vign.addColorStop(0, "rgba(0,0,0,0)");
    vign.addColorStop(1, `rgba(0,0,0,${0.45 * k})`);
    ctx.fillStyle = vign;
    ctx.fillRect(0, 0, W, H);
    disegnaPillolaIcona(ctx, o, u, "rallenta", o.rtl ? 24 * u : W - 24 * u, metà, o.rtl ? "sinistra" : "destra");
  }

  if (sovr.includes("accelerato")) {
    const testo = `×${Math.round(seg.velocita)}`;
    disegnaPillolaIcona(ctx, o, u, "avanti", o.rtl ? 24 * u : W - 24 * u, metà, o.rtl ? "sinistra" : "destra", testo);
  }

  // v038: da solo il marchio va in basso (sotto il mento), lontano dalla faccia
  if (sovr.includes("marchio")) disegnaMarchio(ctx, o, g, u, o.solo ? H * 0.9 : metà);

  if (sovr.includes("conto")) {
    const mancano = s.montaggio.viaMs - s.sorgenteMs; // ms al VIA
    const numero = Math.max(1, Math.ceil(mancano / 1000));
    const fase = 1 - (mancano / 1000 - Math.floor(mancano / 1000)); // 0 appena cambia il numero
    const scala = 0.7 + 0.3 * facile(Math.min(1, fase * 3));
    disegnaDisco(ctx, o, W / 2, metà, 120 * u * scala, String(numero), 150 * u * scala);
  }
  if (sovr.includes("via")) {
    const dopo = s.tClipMs - (s.montaggio.segmenti.find((x) => x.tipo !== "conto")?.inizioClipMs ?? 0);
    const k = Math.max(0, Math.min(1, dopo / 150));
    const svanisci = Math.max(0, Math.min(1, (700 - dopo) / 200));
    ctx.globalAlpha = svanisci;
    const t = o.testi.via;
    ctx.font = `900 ${Math.round(130 * u)}px ${o.caratteri.titolo}`;
    const dim = adatta(ctx, t, o.caratteri.titolo, 900, 130 * u * (0.7 + 0.3 * k), W - 120 * u);
    ctx.font = `900 ${Math.round(dim)}px ${o.caratteri.titolo}`;
    const lw = ctx.measureText(t).width;
    ctx.save();
    ctx.translate(W / 2, metà);
    ctx.rotate(-0.05);
    rettangoloTondo(ctx, -lw / 2 - 40 * u, -dim * 0.65, lw + 80 * u, dim * 1.3, 30 * u);
    ctx.fillStyle = o.colori.primario;
    ctx.fill();
    ctx.lineWidth = 8 * u;
    ctx.strokeStyle = o.colori.suPrimario;
    ctx.stroke();
    ctx.fillStyle = o.colori.suPrimario;
    ctx.textAlign = "center";
    ctx.fillText(t, 0, dim * 0.04);
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  if (sovr.includes("timbro")) {
    const perdeSopra = s.montaggio.perdente === "sopra";
    const p = Math.min(1, s.dentroMs / 260);
    const p2 = Math.min(1, Math.max(0, (s.dentroMs - 180) / 260));
    // In alto in ogni metà (sulla fronte): occhi e bocca — cioè la risata — restano visibili.
    if (o.griglia) {
      // v042: un timbro più piccolo in alto nel riquadro di chi ha riso e in quello di chi ha vinto
      const c = s.celle ?? { riso: null, vinto: null };
      const centro = (k: number) => [(k % 2) * (W / 2) + W / 4, Math.floor(k / 2) * metà + metà * 0.18] as const;
      const oc = { ...o, larghezza: W / 2 };
      if (c.riso !== null) disegnaTimbro(ctx, oc, u * 0.6, ...centro(c.riso), "riso", p);
      if (c.vinto !== null && c.vinto !== c.riso) disegnaTimbro(ctx, oc, u * 0.6, ...centro(c.vinto), "vinto", p2);
    } else if (o.solo) disegnaTimbro(ctx, o, u, W / 2, H * 0.1, perdeSopra ? "vinto" : "riso", p);
    else {
      disegnaTimbro(ctx, o, u, W / 2, metà * 0.2, perdeSopra ? "riso" : "vinto", perdeSopra ? p : p2);
      disegnaTimbro(ctx, o, u, W / 2, metà + metà * 0.2, perdeSopra ? "vinto" : "riso", perdeSopra ? p2 : p);
    }
  }
  ctx.restore();
}

function disegnaDisco(ctx: Contesto2D, o: OpzioniClip, x: number, y: number, r: number, testo: string, px: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = o.colori.primario;
  ctx.fill();
  ctx.lineWidth = r * 0.08;
  ctx.strokeStyle = o.colori.suPrimario;
  ctx.stroke();
  ctx.fillStyle = o.colori.suPrimario;
  ctx.textAlign = "center";
  ctx.font = `900 ${Math.round(px)}px ${o.caratteri.titolo}`;
  ctx.fillText(testo, x, y + px * 0.04);
}

/** Pillola con un'icona disegnata (⏩ «avanti» o «rallenta»), ancorata a destra o a sinistra. */
function disegnaPillolaIcona(ctx: Contesto2D, o: OpzioniClip, u: number, icona: "avanti" | "rallenta", x: number, y: number, lato: "destra" | "sinistra", testo = ""): void {
  ctx.font = `800 ${Math.round(34 * u)}px ${o.caratteri.testo}`;
  const tw = testo ? ctx.measureText(testo).width + 12 * u : 0;
  const w = 74 * u + tw;
  const h = 60 * u;
  const x0 = lato === "destra" ? x - w : x;
  rettangoloTondo(ctx, x0, y - h / 2, w, h, h / 2);
  ctx.fillStyle = o.colori.primario;
  ctx.fill();
  ctx.fillStyle = o.colori.suPrimario;
  const ix = x0 + 18 * u;
  if (icona === "avanti") {
    // Due triangoli affiancati: l'icona «avanti veloce» disegnata, non un'emoji.
    for (const dx of [0, 19 * u]) {
      ctx.beginPath();
      ctx.moveTo(ix + dx, y - 14 * u);
      ctx.lineTo(ix + dx + 19 * u, y);
      ctx.lineTo(ix + dx, y + 14 * u);
      ctx.closePath();
      ctx.fill();
    }
  } else {
    // «Rallenta»: un triangolo con due linee di moto dietro.
    ctx.beginPath();
    ctx.moveTo(ix + 16 * u, y - 14 * u);
    ctx.lineTo(ix + 38 * u, y);
    ctx.lineTo(ix + 16 * u, y + 14 * u);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(ix, y - 9 * u, 9 * u, 5 * u);
    ctx.fillRect(ix, y + 4 * u, 9 * u, 5 * u);
  }
  if (testo) {
    ctx.textAlign = "left";
    ctx.direction = "ltr";
    ctx.fillText(testo, x0 + 64 * u, y + 2 * u);
    ctx.direction = o.rtl ? "rtl" : "ltr";
  }
}

/**
 * Il marchio fisso: faccina + il link in una pillola sulla linea tra le due metà (lontano dai volti). v037: per tutta
 * la clip c'è il link di chi l'ha fatta («laughdown.net/@giulia»), non solo il sito: chi guarda sa chi sfidare.
 */
function disegnaMarchio(ctx: Contesto2D, o: OpzioniClip, g: Grafica, u: number, metà: number): void {
  const testo = o.link || "laughdown.net";
  ctx.font = `800 ${Math.round(adatta(ctx, testo, o.caratteri.testo, 800, 26 * u, o.larghezza * 0.62))}px ${o.caratteri.testo}`;
  const tw = ctx.measureText(testo).width;
  const icona = 40 * u;
  const w = 14 * u + icona + 10 * u + tw + 18 * u;
  const h = 54 * u;
  const x0 = o.rtl ? o.larghezza - 24 * u - w : 24 * u;
  ctx.globalAlpha = 0.92;
  rettangoloTondo(ctx, x0, metà - h / 2, w, h, h / 2);
  ctx.fillStyle = o.colori.sfondo;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 3 * u;
  ctx.strokeStyle = o.colori.testo;
  ctx.stroke();
  if (g.simbolo) ctx.drawImage(g.simbolo, x0 + 14 * u, metà - icona / 2, icona, icona);
  ctx.fillStyle = o.colori.testo;
  ctx.textAlign = "left";
  ctx.direction = "ltr"; // il dominio si scrive sempre da sinistra a destra
  ctx.fillText(testo, x0 + 14 * u + icona + 10 * u, metà + 1 * u);
  ctx.direction = o.rtl ? "rtl" : "ltr";
}

/** Timbro inclinato: «HA RISO!» pieno nel colore di pericolo, «HA VINTO!» con luminanza opposta (chiaro/scuro). */
function disegnaTimbro(ctx: Contesto2D, o: OpzioniClip, u: number, x: number, y: number, quale: "riso" | "vinto", p: number): void {
  if (p <= 0) return;
  const testo = quale === "riso" ? o.testi.haRiso : o.testi.haVinto;
  const pericoloScuro = luminanza(o.colori.pericolo) < 0.35;
  let fondo: string;
  let inchiostro: string;
  let bordo: string;
  if (quale === "riso") {
    fondo = o.colori.pericolo;
    inchiostro = contrasto(o.colori.pericolo);
    bordo = inchiostro;
  } else {
    // Luminanza opposta a quella del timbro «HA RISO!», così si distinguono anche senza vedere i colori.
    fondo = pericoloScuro ? CHIARO : SCURO;
    inchiostro = luminanza(o.colori.successo) > 0.35 === !pericoloScuro ? o.colori.successo : contrasto(fondo);
    bordo = o.colori.successo;
  }
  const angolo = quale === "riso" ? -0.14 : 0.09;
  const scala = rimbalzo(p) * (quale === "riso" ? 1 : 0.92);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angolo);
  ctx.scale(scala, scala);
  ctx.globalAlpha = Math.min(1, p * 2);
  const dim = adatta(ctx, testo, o.caratteri.titolo, 900, 112 * u, o.larghezza - 170 * u);
  ctx.font = `900 ${Math.round(dim)}px ${o.caratteri.titolo}`;
  const lw = ctx.measureText(testo).width;
  const w = lw + 70 * u;
  const h = dim * 1.35;
  rettangoloTondo(ctx, -w / 2, -h / 2, w, h, 18 * u);
  ctx.fillStyle = fondo;
  ctx.fill();
  ctx.lineWidth = 10 * u;
  ctx.strokeStyle = bordo;
  ctx.stroke();
  rettangoloTondo(ctx, -w / 2 + 12 * u, -h / 2 + 12 * u, w - 24 * u, h - 24 * u, 10 * u);
  ctx.lineWidth = 3 * u;
  ctx.strokeStyle = inchiostro;
  ctx.stroke();
  ctx.fillStyle = inchiostro;
  ctx.textAlign = "center";
  ctx.fillText(testo, 0, dim * 0.05);
  ctx.restore();
}

/** I secondi finali: fondo del tema, simbolo grande e «Sfidami: link» (il link su una riga a parte, evidenziato). */
function disegnaFinale(ctx: Contesto2D, o: OpzioniClip, g: Grafica, dentroMs: number, durataMs: number): void {
  const W = o.larghezza;
  const H = o.altezza;
  const u = W / 720;
  ctx.fillStyle = o.colori.sfondo;
  ctx.fillRect(0, 0, W, H);
  const entra = facile(Math.min(1, dentroMs / 400));
  const lato = 300 * u * (0.6 + 0.4 * entra);
  const rimbalza = Math.sin(Math.min(1, dentroMs / Math.max(1, durataMs)) * Math.PI * 4) * 6 * u * (1 - entra * 0.5);
  if (g.simbolo) ctx.drawImage(g.simbolo, (W - lato) / 2, H * 0.36 - lato / 2 + rimbalza, lato, lato);
  else {
    ctx.beginPath();
    ctx.arc(W / 2, H * 0.36, lato / 2, 0, Math.PI * 2);
    ctx.fillStyle = o.colori.accento;
    ctx.fill();
  }
  // «Sfidami: {{link}}» → prima riga il testo, seconda riga il link.
  const [prima = "", dopo = ""] = o.testi.sfidami.split("{{link}}");
  const riga1 = `${prima.trim()} ${dopo.trim()}`.trim();
  ctx.globalAlpha = entra;
  ctx.textAlign = "center";
  ctx.fillStyle = o.colori.testo;
  if (riga1) {
    const d1 = adatta(ctx, riga1, o.caratteri.titolo, 900, 76 * u, W - 100 * u);
    ctx.font = `900 ${Math.round(d1)}px ${o.caratteri.titolo}`;
    ctx.fillText(riga1, W / 2, H * 0.6);
  }
  const d2 = adatta(ctx, o.link, o.caratteri.testo, 800, 54 * u, W - 120 * u);
  ctx.font = `800 ${Math.round(d2)}px ${o.caratteri.testo}`;
  const lw = ctx.measureText(o.link).width;
  rettangoloTondo(ctx, W / 2 - lw / 2 - 30 * u, H * 0.69 - d2 * 0.85, lw + 60 * u, d2 * 1.7, d2 * 0.85);
  ctx.fillStyle = o.colori.primario;
  ctx.fill();
  ctx.fillStyle = o.colori.suPrimario;
  ctx.direction = "ltr";
  ctx.fillText(o.link, W / 2, H * 0.69 + d2 * 0.04);
  // v037: il codice QR del link sotto (si inquadra dallo schermo di un altro telefono)
  if (g.qr) {
    const q = 210 * u;
    const x = (W - q) / 2;
    const y = H * 0.76;
    rettangoloTondo(ctx, x - 14 * u, y - 14 * u, q + 28 * u, q + 28 * u, 22 * u);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.drawImage(g.qr, x, y, q, q);
  }
  ctx.globalAlpha = 1;
}

/** v037: il QR del link come tela (una volta per clip), nero su bianco. */
export function telaQr(testo: string): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  try {
    const q = qrcode(0, "M");
    q.addData(testo);
    q.make();
    const n = q.getModuleCount();
    const m = 8;
    const tela = document.createElement("canvas");
    tela.width = tela.height = (n + 2) * m;
    const c = tela.getContext("2d")!;
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, tela.width, tela.height);
    c.fillStyle = "#0e0a24";
    for (let r = 0; r < n; r++) for (let k = 0; k < n; k++) if (q.isDark(r, k)) c.fillRect((k + 1) * m, (r + 1) * m, m, m);
    return tela;
  } catch {
    return null;
  }
}
